/**
 * CustodyChip — compact card showing "{ItemName} • Held by {Name}".
 *
 * Renders inside an AssistantBubble when a `who_has_item` tool
 * resolved.  Tap navigates to ItemDetail with the item id.  Uses a
 * person glyph + a colour cue (purple = self-custody, amber = lent
 * out) so a glance tells the user whether they have it.
 */

import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { fpColors } from '../../../../styles/theme';
import type { CustodyArtifact } from '../../api/parseArtifactsFromToolEvent';

interface Props {
  artifact: CustodyArtifact;
}

function formatSince(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Compact relative-time copy
  const now = Date.now();
  const ms = now - d.getTime();
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export const CustodyChip: React.FC<Props> = ({ artifact }) => {
  const navigation = useNavigation<any>();
  const cueColor = artifact.is_self_custody
    ? fpColors.primary
    : // Amber cue for "lent out" — theme doesn't expose a warning
      // token, hardcode for now (matches the borrowRequests amber).
      '#B98700';
  const since = formatSince(artifact.since);
  return (
    <Pressable
      style={({ pressed }) => [
        styles.row,
        { borderLeftColor: cueColor },
        pressed && styles.rowPressed,
      ]}
      onPress={() =>
        navigation.navigate('ItemDetail', { itemId: artifact.item_id })
      }
      accessibilityRole="button"
      accessibilityLabel={`Open ${artifact.item_name}`}
      testID={`founsi-custody-${artifact.item_id}`}
    >
      <View style={[styles.iconWrap, { backgroundColor: cueColor + '20' }]}>
        <MaterialIcons
          name={artifact.is_self_custody ? 'person' : 'swap-horiz'}
          size={16}
          color={cueColor}
        />
      </View>
      <View style={styles.body}>
        <Text style={styles.title} numberOfLines={1}>
          {artifact.item_name}
        </Text>
        <Text style={styles.sub} numberOfLines={1}>
          {artifact.is_self_custody ? 'You have it' : `Held by ${artifact.custodian_name}`}
          {since ? ` · ${since}` : ''}
        </Text>
      </View>
      <MaterialIcons
        name="chevron-right"
        size={18}
        color={fpColors.onSurfaceVariant}
      />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginBottom: 6,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderLeftWidth: 3,
  },
  rowPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  iconWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
  },
  title: {
    fontSize: 13,
    fontWeight: '600',
    color: fpColors.onSurface,
  },
  sub: {
    marginTop: 1,
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
});

export default CustodyChip;
